import React from "react";
import * as Icons from "lucide-react";
import PageHero from "../components/PageHero";
import QuoteForm from "../components/QuoteForm";
import CallToActionBanner from "../components/CallToActionBanner";
import Editable from "../components/Editable";
import { company, heroImages, aboutContent, partnerReasons, services, strengths } from "../mock";
import SEO from "../components/SEO";

export default function About() {
  return (
    <>
      <SEO
        title="About Us — Global Network, Local Delivery"
        description="Learn about Ruken Alazdehar General Trading LLC — a Dubai-based global trading house sourcing high-grade Clinker, Limestone, Gypsum and Slag through exclusive producer partnerships since 2020."
        path="/about-us"
      />
      <PageHero
        title="About Ruken Alazdehar"
        subtitle={aboutContent.aboutHeadline + " — a vital link between major mineral producers and the world's leading raw material manufacturers."}
        breadcrumbs={[{ label: "About Us" }]}
        bgImage={heroImages.about}
      />

      {/* ABOUT US */}
      <section id="about" className="section-padding bg-white scroll-mt-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative fade-in">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-xl" style={{ background: "var(--brand-100)" }} />
            <img
              src="/images/about-global-network.webp"
              alt="Ruken Alazdehar operations"
              className="relative rounded-xl shadow-xl w-full aspect-[4/5] object-cover"
            />
          </div>
          <div className="fade-in">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>About Us</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-brand mt-2 leading-tight" style={{ color: "var(--brand-700)" }}>
              <Editable field="about.headline" as="span">{aboutContent.aboutHeadline}</Editable>.
            </h2>
            <Editable field="about.intro" as="p" className="mt-5 text-muted-foreground text-[15px] leading-relaxed" multiline>{aboutContent.aboutIntro}</Editable>
            <Editable field="about.intro2" as="p" className="mt-4 text-muted-foreground text-[15px] leading-relaxed" multiline>{aboutContent.aboutIntro2}</Editable>

            {/* Mini fact strip */}
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { Icon: Icons.MapPin, label: "Headquarters", value: "Dubai, UAE" },
                { Icon: Icons.Calendar, label: "Since", value: "2020" },
                { Icon: Icons.Globe2, label: "Reach", value: "Global" },
              ].map((f, i) => (
                <div key={i} className="p-4 rounded-lg border border-border bg-muted/20">
                  <f.Icon size={18} style={{ color: "var(--brand-500)" }} />
                  <p className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">{f.label}</p>
                  <p className="text-sm font-semibold text-brand" style={{ color: "var(--brand-700)" }}>{f.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section id="who" className="section-padding scroll-mt-28" style={{ background: "var(--brand-50)" }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="fade-in">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>Who We Are</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-brand mt-2 leading-tight" style={{ color: "var(--brand-700)" }}>
              <Editable field="about.whoHeadline" as="span">{aboutContent.whoHeadline}</Editable>.
            </h2>
            <Editable field="about.whoBody" as="p" className="mt-5 text-foreground/80 text-[15px] leading-relaxed" multiline>{aboutContent.whoBody}</Editable>
            <Editable field="about.whoBody2" as="p" className="mt-4 text-foreground/80 text-[15px] leading-relaxed" multiline>{aboutContent.whoBody2}</Editable>
          </div>
          <div className="fade-in">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold mb-4" style={{ color: "var(--brand-500)" }}>Why Partners Choose Us</p>
            <div className="space-y-4">
              {partnerReasons.map((r) => {
                const Icon = Icons[r.icon] || Icons.CheckCircle2;
                return (
                  <div key={r.title} className="p-6 rounded-xl bg-white border border-border card-hover">
                    <div className="flex items-start gap-4">
                      <span className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}>
                        <Icon size={22} style={{ color: "var(--brand-700)" }} />
                      </span>
                      <div>
                        <h3 className="font-display font-semibold text-lg text-brand" style={{ color: "var(--brand-700)" }}>{r.title}</h3>
                        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{r.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section id="what" className="section-padding bg-white scroll-mt-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14 fade-in">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>What We Do</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-brand mt-2 leading-tight" style={{ color: "var(--brand-700)" }}>
              End-to-End Commodity Supply Solutions
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Global sourcing from deep-rooted origination points, strict due diligence and best-in-class vessel loading logistics for on-time delivery.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Box;
              return (
                <div key={i} className="group bg-white border border-border rounded-xl p-8 card-hover fade-in">
                  <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-5" style={{ background: "var(--brand-100)" }}>
                    <Icon size={26} style={{ color: "var(--brand-700)" }} />
                  </div>
                  <h3 className="font-display text-xl font-bold text-brand mb-3" style={{ color: "var(--brand-700)" }}>{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW WE ADD VALUE */}
      <section id="value" className="section-padding scroll-mt-28" style={{ background: "var(--brand-50)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14 fade-in">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>How We Add Value</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-brand mt-2 leading-tight" style={{ color: "var(--brand-700)" }}>
              Seven pillars of a reputed global trading firm.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Supported by a worldwide network of strategically located sea ports and strong relationships with reliable manufacturers and suppliers.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {strengths.map((s) => {
              const Icon = Icons[s.icon] || Icons.CheckCircle2;
              return (
                <div key={s.title} className="p-6 rounded-xl border border-border bg-white card-hover fade-in">
                  <Icon size={26} style={{ color: "var(--brand-700)" }} />
                  <h3 className="mt-4 font-display font-semibold text-lg text-brand" style={{ color: "var(--brand-700)" }}>{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section id="mission" className="section-padding bg-white scroll-mt-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl border border-border bg-muted/20 fade-in">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-5" style={{ background: "var(--brand-100)" }}>
                <Icons.Target size={28} style={{ color: "var(--brand-700)" }} />
              </div>
              <Editable field="about.mission.title" as="h3" className="font-display text-2xl font-bold text-brand mb-3" style={{ color: "var(--brand-700)" }}>Our Mission</Editable>
              <Editable field="about.mission" as="p" className="text-muted-foreground leading-relaxed" multiline>{aboutContent.mission}</Editable>
            </div>
            <div className="p-8 rounded-2xl border border-border bg-muted/20 fade-in">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-5" style={{ background: "var(--brand-100)" }}>
                <Icons.Eye size={28} style={{ color: "var(--brand-700)" }} />
              </div>
              <Editable field="about.vision.title" as="h3" className="font-display text-2xl font-bold text-brand mb-3" style={{ color: "var(--brand-700)" }}>Our Vision</Editable>
              <Editable field="about.vision" as="p" className="text-muted-foreground leading-relaxed" multiline>{aboutContent.vision}</Editable>
            </div>
          </div>
        </div>
      </section>

      {/* Let's connect + form */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div className="fade-in">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>Let's Connect</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-brand mt-2 leading-tight" style={{ color: "var(--brand-700)" }}>Contact us for assistance and estimates.</h2>
            <ul className="mt-8 space-y-5">
              <li className="flex gap-4"><span className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}><Icons.MapPin size={18} style={{ color: "var(--brand-700)" }} /></span><div><p className="text-xs uppercase tracking-wider text-muted-foreground">Office</p><p className="font-medium text-foreground">{company.address}</p></div></li>
              <li className="flex gap-4"><span className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}><Icons.Phone size={18} style={{ color: "var(--brand-700)" }} /></span><div><p className="text-xs uppercase tracking-wider text-muted-foreground">Phone</p><p className="font-medium text-foreground">{company.phone}</p>{company.landline && <p className="text-sm text-foreground/70 mt-0.5">Landline: {company.landline}</p>}</div></li>
              <li className="flex gap-4"><span className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}><Icons.Mail size={18} style={{ color: "var(--brand-700)" }} /></span><div><p className="text-xs uppercase tracking-wider text-muted-foreground">Email</p><p className="font-medium text-foreground">{company.email}</p></div></li>
              <li className="flex gap-4"><span className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}><Icons.Clock size={18} style={{ color: "var(--brand-700)" }} /></span><div><p className="text-xs uppercase tracking-wider text-muted-foreground">Working Hours</p><p className="font-medium text-foreground">{company.hours}</p></div></li>
            </ul>
          </div>
          <div className="fade-in">
            <QuoteForm />
          </div>
        </div>
      </section>
      <CallToActionBanner />
    </>
  );
}
